import { loadGovernedBiContext, normalizeBiConfidence, normalizeBiKey, type BiAudience, type BiRpcClient, type GovernedBiContext } from "@/lib/governedBiRetrieval";
import { buildBehaviorConstellationStates, type ConstellationProjectionObservation } from "../../../supabase/functions/_shared/behaviorConstellationProjection";
type BehaviorCatalogItem={id:string;name:string;theme:"CONNECT"|"OWN"|"RESPOND"|"ELEVATE"};
type QueryError = {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
} | null;

type QueryResponse<T> = {
  data: T[] | null;
  error: QueryError;
};

type QueryBuilder<T> = {
  select: (columns: string) => QueryBuilder<T>;
  eq: (column: string, value: string) => QueryBuilder<T>;
  gte: (column: string, value: string) => QueryBuilder<T>;
  order: (column: string, options: { ascending: boolean }) => QueryBuilder<T>;
  then: (resolve: (value: QueryResponse<T>) => unknown) => Promise<unknown>;
};

export type MinimalSupabaseClient = {
  from: <T>(table: string) => QueryBuilder<T>;
};

export type ConstellationStateRow = {
  behavior_id: string;
  theme: "CONNECT" | "OWN" | "RESPOND" | "ELEVATE";
  supporting_meeting_count_30d: number;
  opposing_meeting_count_30d: number;
  support_score: number;
  opposition_score: number;
  net_intensity: number;
  state: "dormant" | "emerging" | "strengthening" | "mixed" | "receding";
  last_supported_at: string | null;
  last_opposed_at: string | null;
  evidence_meeting_ids: string[] | null;
  context_tags: string[] | null;
};

export type ObservationRow = {
  behavior_id: string;
  meeting_id: string;
  polarity: "supporting" | "opposing";
  source_type: "transcript" | "context_graph" | "behavioral_intelligence";
  confidence: "low" | "medium" | "high";
  evidence_excerpt: string | null;
  context_tags: string[] | null;
  observed_at: string;
  meetings?: { title: string | null; start_time: string | null } | null;
};

type BehavioralThemeProjectionRow = {
  leader_id: string;
  theme_name: string;
  confidence_score: number;
  confidence_level: "low" | "medium" | "high";
  last_seen_at: string;
  occurrence_count: number;
  supporting_meeting_count: number;
  supporting_artifact_count: number;
  status: string;
  metadata: Record<string, unknown> | null;
};

type ObservationProjectionRow = {
  id: string;
  leader_id: string;
  meeting_id: string | null;
  behavior_theme: string;
  source_artifact_id: string;
  source_type: string;
  confidence_level: "low" | "medium" | "high";
  created_at: string;
  contradicts_observation_id: string | null;
  metadata: Record<string, unknown> | null;
};

export type LivingConstellationPersistedLoad = {
  governedBi: GovernedBiContext;
  constellationRows: ConstellationStateRow[];
  observationRows: ObservationRow[];
  degradedModes: {
    missingConstellationTable: boolean;
    missingObservationTable: boolean;
  };
};

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function mapThemeStatusToConstellationState(row: BehavioralThemeProjectionRow): ConstellationStateRow["state"] {
  const metadata = row.metadata ?? {};
  const contradictionCount = asNumber(metadata.contradiction_count) ?? 0;
  if (contradictionCount > 0 || row.status === "contradicted" || metadata.contradiction_flag === true) return "mixed";
  if (row.status === "strengthening") return "strengthening";
  if (row.supporting_meeting_count >= 3 || row.confidence_score >= 0.75) return "strengthening";
  if (row.supporting_meeting_count >= 1 || row.confidence_score > 0) return "emerging";
  return "dormant";
}

function indexBehaviorCatalog(behaviors: BehaviorCatalogItem[]): Map<string,BehaviorCatalogItem|null> {
  const catalog=new Map<string,BehaviorCatalogItem|null>();
  for (const behavior of behaviors) {
    const key=normalizeBiKey(behavior.name);
    if (!key) continue;
    const prior=catalog.get(key);
    // Match governance SQL: one CORE domain and the lowest stable catalog ID.
    // Conflicting domains remain unmapped even if a later row matches one side.
    if (catalog.has(key) && (!prior || prior.theme !== behavior.theme)) catalog.set(key,null);
    else if (!prior || behavior.id<prior.id) catalog.set(key,behavior);
  }
  return catalog;
}

function deriveConstellationRowsFromV2Themes(rows: BehavioralThemeProjectionRow[],catalog: Map<string,BehaviorCatalogItem|null>): ConstellationStateRow[] {
  return rows.flatMap((row) => {
    const metadata = row.metadata ?? {};
    const behavior=catalog.get(normalizeBiKey(row.theme_name));
    if (!behavior) return [];
    const behaviorId=behavior.id, theme=behavior.theme;

    const contradicted=row.status === "contradicted" || metadata.contradiction_flag === true;
    const contradictionCount = Math.max(contradicted ? 1 : 0,asNumber(metadata.contradiction_count) ?? 0);
    const oppositionScore = contradictionCount > 0 ? Math.min(0.5, contradictionCount * 0.1) : 0;
    const supportScore = contradictionCount > 0 ? 0 : Number(row.confidence_score.toFixed(3));
    return [{
      behavior_id: behaviorId,
      theme,
      supporting_meeting_count_30d: row.supporting_meeting_count,
      opposing_meeting_count_30d: contradictionCount,
      support_score: supportScore,
      opposition_score: oppositionScore,
      net_intensity: Number(Math.max(0, supportScore - oppositionScore).toFixed(3)),
      state: mapThemeStatusToConstellationState(row),
      last_supported_at: row.last_seen_at,
      last_opposed_at: asNullableString(metadata.last_opposed_at),
      evidence_meeting_ids: asStringArray(metadata.evidence_meeting_ids),
      context_tags: asStringArray(metadata.context_tags),
    }];
  });
}

function deriveObservationRowsFromV2Observations(rows: ObservationProjectionRow[],catalog: Map<string,BehaviorCatalogItem|null>): ObservationRow[] {
  return rows.flatMap((row) => {
    const metadata = row.metadata ?? {};
    const behaviorId = catalog.get(normalizeBiKey(row.behavior_theme))?.id;
    const meetingId = row.meeting_id ?? asNullableString(metadata.meeting_id);
    if (!behaviorId || !meetingId) return [];
    const polarityValue = asNullableString(metadata.polarity);
    const polarity: ObservationRow["polarity"] = ["opposing","contradicts"].includes(polarityValue ?? "") || row.contradicts_observation_id
      ? "opposing"
      : "supporting";

    return [{
      behavior_id: behaviorId,
      meeting_id: meetingId,
      polarity,
      source_type: "behavioral_intelligence",
      confidence: row.confidence_level,
      evidence_excerpt: asNullableString(metadata.evidence_excerpt),
      context_tags: asStringArray(metadata.context_tags),
      observed_at: row.created_at,
      meetings: {
        title: asNullableString(metadata.meeting_title),
        start_time: asNullableString(metadata.meeting_start_time) ?? row.created_at,
      },
    }];
  });
}

export function isMissingSchemaObject(error: unknown): boolean {
  const err = error as QueryError;
  if (!err) return false;
  if (err.code === "42P01" || err.code === "PGRST204" || err.code === "PGRST205") return true;
  const message = `${err.message || ""} ${err.details || ""} ${err.hint || ""}`.toLowerCase();
  const mentionsColumn = message.includes(" column ") || message.includes("column ");
  return (
    (message.includes("does not exist") && !mentionsColumn)
    || message.includes("could not find the table")
    || message.includes("could not find a relationship")
    || message.includes("schema cache")
  );
}

export async function loadPersistedConstellationState(params: {
  supabase: MinimalSupabaseClient;
  leaderId: string;
  historyStart: string;
  audience?: BiAudience;
  behaviors?: BehaviorCatalogItem[];
  nowIso?: string;
  governedBi?: GovernedBiContext;
}): Promise<LivingConstellationPersistedLoad> {
  const {supabase,leaderId,historyStart} = params;
  const catalog=indexBehaviorCatalog(params.behaviors || []);
  const nowIso=params.nowIso ?? new Date().toISOString();
  const windowStart=new Date(Date.parse(nowIso)-30*24*60*60*1000).toISOString();
  const context=params.governedBi??await loadGovernedBiContext(supabase as unknown as BiRpcClient,leaderId,params.audience??'leader');
  if(context.ownerProjection)return {governedBi:context,constellationRows:[],observationRows:[],
    degradedModes:{missingConstellationTable:false,missingObservationTable:false}};
  const observationResult=await supabase.from<ObservationRow>("meeting_behavior_observations")
    .select("behavior_id, meeting_id, polarity, source_type, confidence, evidence_excerpt, context_tags, observed_at, meetings(title,start_time)")
    .eq("leader_id",leaderId).eq("source_type","transcript")
    .gte("observed_at",historyStart).order("observed_at",{ascending:false});
  if (observationResult.error) console.warn("constellation_transcript_read_failed");
  if (observationResult.error && !isMissingSchemaObject(observationResult.error)) throw observationResult.error;
  // Raw persisted states mix transcript and BI without recoverable source lineage.
  // Recompute the same deterministic contract from independently authorized transcript rows.
  const transcriptRows=(observationResult.data || []).filter(row=>row.source_type === "transcript");
  const prior=new Map<string,number>();
  for (const row of transcriptRows) if (row.observed_at<windowStart && row.polarity === "supporting")
    prior.set(row.behavior_id,(prior.get(row.behavior_id) ?? 0)+1);
  const coachRows=context.audience === "coach"
    ? deriveObservationRowsFromV2Observations(context.observations,catalog).filter(row=>Number.isFinite(Date.parse(row.observed_at))
      && Date.parse(row.observed_at)>=Date.parse(historyStart)) : [];
  // BI supporting projections must not boost independently authorized transcripts.
  // Actual admitted opposition uses the same meeting/polarity deduplication as transcripts.
  const safeRows=transcriptRows.concat(coachRows.filter(row=>row.polarity === "opposing"))
    .map(row=>({...row,leader_id:leaderId,context_tags:row.context_tags || []})) satisfies ConstellationProjectionObservation[];
  const constellationRows:ConstellationStateRow[]=buildBehaviorConstellationStates({leaderId,behaviors:params.behaviors || [],
    observationRows30d:safeRows.filter(row=>row.observed_at>=windowStart),priorSupportingCountByBehaviorId:prior,nowIso});
  const approved = context.audience === "leader"
    ? context.safeProjections.flatMap(theme=>theme.behavior_id && theme.core_domain ? [{
        behavior_id:theme.behavior_id,theme:theme.core_domain as ConstellationStateRow["theme"],
        supporting_meeting_count_30d:0,opposing_meeting_count_30d:0,support_score:theme.confidence_score,
        opposition_score:0,net_intensity:theme.confidence_score,state:"emerging" as const,
        last_supported_at:theme.last_seen_at,last_opposed_at:null,evidence_meeting_ids:[],context_tags:[],
      }] : [])
     : deriveConstellationRowsFromV2Themes(context.themes.map(theme=>({...theme,confidence_score:normalizeBiConfidence(theme.confidence_level),
        metadata:{...theme.metadata,contradiction_flag:context.rankThemes.find(rank=>rank.id === theme.theme_id)?.contradicted === true}})),catalog);
  // A theme contradiction can have no mapped meeting. Preserve real counts/scores
  // while showing the current coach challenge; leaders receive no private opposition.
  for (const row of approved) {
    const index=constellationRows.findIndex(value=>value.behavior_id === row.behavior_id);
    if (context.audience === "coach" && row.state === "mixed" && index>=0) {
      if (constellationRows[index].state !== "receding") constellationRows[index]={...constellationRows[index],state:"mixed"};
    } else if (index<0) constellationRows.push(row);
    else if (constellationRows[index].supporting_meeting_count_30d === 0 && constellationRows[index].opposing_meeting_count_30d === 0) constellationRows[index]=row;
  }
  const transcriptKeys=new Set(transcriptRows.map(row=>JSON.stringify([row.behavior_id,row.meeting_id,row.polarity])));
  const observationRows=transcriptRows.concat(coachRows.filter(row=>!transcriptKeys.has(JSON.stringify([row.behavior_id,row.meeting_id,row.polarity]))))
    .sort((a,b)=>Date.parse(b.observed_at)-Date.parse(a.observed_at));
  const governedBi=context;
  return {constellationRows,observationRows,governedBi,
    degradedModes:{missingConstellationTable:false,missingObservationTable:!!observationResult.error}};
}
